import { createContext, useContext, ReactNode, useCallback, useEffect } // Added useEffect
from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { User, signOut as authSignOut } from '../services/authService'; // User type must include 'name'
import { useUserSession } from '../hooks/useUserSession';
import { useLocation, useNavigate } from 'react-router-dom'; // Added

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  // login method removed
  logout: () => Promise<void>;
  isLoading: boolean;
}

const URL_SERVER = import.meta.env.VITE_API_BASE_URL;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate(); // Added
  const location = useLocation(); // Added

  const { data: user, isLoading, error } = useUserSession();

  useEffect(() => {
    console.log({error})
    if (error) {
      console.error('Error fetching user session (AuthProvider):', error.message);
      // This was the original line:
      window.location.href = `${URL_SERVER}/auth/sign-in`;
      // This line can cause issues if the error is not a 401, or if we're on a public page.
      // It's generally better to handle redirects within the React Router flow.
    }
  }, [error]);


  useEffect(() => {
    if (!isLoading && user) { // User is loaded and exists
      if (!user.name && location.pathname !== '/complete-profile') {
        navigate('/complete-profile', { replace: true, state: { from: location } });
      } else if (user.name && location.pathname === '/complete-profile') {
        navigate(location.state?.from?.pathname || '/dashboard', { replace: true });
      }
    }
    // No explicit redirect for !user here, as ProtectedRoute or App.tsx should handle
    // redirecting to a login page or showing login prompts if user is null and not on a public route.
    // The aggressive redirect for `error` above was removed to prevent loops and allow more granular control.
  }, [user, isLoading, navigate, location]);

  const isAuthenticated = !!user;

  const logout = useCallback(async () => {
    try {
      await authSignOut(); // Assuming authSignOut might be async
      queryClient.setQueryData(['userSession'], null);
      // Optionally, navigate to login page after logout
      // navigate('/auth/sign-in', { replace: true }); // Or your login route
      console.log('AuthContext: User signed out, session data set to null.');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  }, [queryClient, authSignOut]); // navigate removed from here as it's not used in this version of logout

  if (isLoading) {
    return <><p>Loading...</p></>; // Or a loading spinner/skeleton
  }

  if(!isLoading && !user){
    // If not loading and no user, we can choose to render nothing or a placeholder.
    // The redirect to login should be handled by the routing logic or a higher-level component.
    window.location.href = `${URL_SERVER}/auth/sign-in`;
    return <></>;
  }

  return (
    <AuthContext.Provider
      value={{
        user: user || null,
        isAuthenticated,
        logout,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
